import { http, HttpResponse, delay } from 'msw';
import {
  InvoiceStatus,
  TransactionType,
  TransactionSourceType,
} from '@electricjs/core_entity-client';

import {
  billingInfo,
  invoicesList,
  licenseCountAmendment,
  noPendingSubscriptionChange,
  paymentMethodsV1,
  paymentMethodsV3,
  paymentTerms,
  taxesEstimation,
} from '@/mocks/data/billingData';

export const billingHandlers = [
  http.get(
    `*/v1/organizations/:organizationId/invoices`,
    async ({ request }) => {
      const url = new URL(request.url);
      const searchParams = url.searchParams;

      await delay();

      if (searchParams.get('check_documents') === 'true') {
        return HttpResponse.json(invoicesList, { status: 200 });
      }
      // We don't have a non check_documents mock for now, so we just return the same data
      return HttpResponse.json(invoicesList, { status: 200 });
    }
  ),
  // V2 Invoices endpoint (used by InvoicesTile)
  http.get(`*/v2/organizations/:organizationId/invoices`, async () => {
    await delay();
    return HttpResponse.json(
      {
        invoices: [
          {
            invoice_id: 'inv-001',
            organization_id: 'org-123',
            invoice_number: 'INV-001',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Paid,
            description: 'Subscription payment',
            hosted_invoice_url: 'https://example.com/invoice/1',
            pdf_url: 'https://example.com/invoice/1.pdf',
            due_date: '2024-01-05T00:00:00Z',
            amount_paid: 500,
            amount_total: 500,
            user_id: 'user-123',
            created_at: '2024-01-06T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-002',
            organization_id: 'org-123',
            invoice_number: 'INV-002',
            transaction_type: TransactionType.HardwarePurchase,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Open,
            description: 'Hardware purchase',
            hosted_invoice_url: 'https://example.com/invoice/2',
            pdf_url: 'https://example.com/invoice/2.pdf',
            due_date: '2024-01-20T00:00:00Z',
            amount_paid: 0,
            amount_total: 1200,
            user_id: 'user-123',
            created_at: '2024-01-05T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-003',
            organization_id: 'org-123',
            invoice_number: 'INV-003',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Open,
            description: 'Subscription - overdue',
            hosted_invoice_url: 'https://example.com/invoice/3',
            pdf_url: 'https://example.com/invoice/3.pdf',
            due_date: '2024-01-01T00:00:00Z',
            amount_paid: 0,
            amount_total: 750,
            user_id: 'user-123',
            created_at: '2024-01-04T00:00:00Z',
            is_overdue: true,
          },
          {
            invoice_id: 'inv-004',
            organization_id: 'org-123',
            invoice_number: 'INV-004',
            transaction_type: TransactionType.AdhocInvoice,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Void,
            description: 'Voided invoice',
            hosted_invoice_url: 'https://example.com/invoice/4',
            pdf_url: 'https://example.com/invoice/4.pdf',
            due_date: '2024-01-10T00:00:00Z',
            amount_paid: 0,
            amount_total: 300,
            user_id: 'user-123',
            created_at: '2024-01-03T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-005',
            organization_id: 'org-123',
            invoice_number: 'INV-005',
            transaction_type: TransactionType.HardwarePurchase,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Uncollectible,
            description: 'Uncollectible hardware invoice',
            hosted_invoice_url: 'https://example.com/invoice/5',
            pdf_url: 'https://example.com/invoice/5.pdf',
            due_date: '2023-12-01T00:00:00Z',
            amount_paid: 0,
            amount_total: 2500,
            user_id: 'user-123',
            created_at: '2024-01-02T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-006',
            organization_id: 'org-123',
            invoice_number: 'INV-006',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Draft,
            description: 'Draft invoice',
            hosted_invoice_url: 'https://example.com/invoice/6',
            pdf_url: 'https://example.com/invoice/6.pdf',
            due_date: '2024-02-01T00:00:00Z',
            amount_paid: 0,
            amount_total: 450,
            user_id: 'user-123',
            created_at: '2024-01-01T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-007',
            organization_id: 'org-123',
            invoice_number: 'INV-007',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Open,
            description: 'Overdue subscription invoice',
            hosted_invoice_url: 'https://example.com/invoice/7',
            pdf_url: 'https://example.com/invoice/7.pdf',
            due_date: '2026-02-15T00:00:00Z',
            amount_paid: 0,
            amount_total: 600,
            user_id: 'user-123',
            created_at: '2026-02-01T00:00:00Z',
            is_overdue: true,
          },
          {
            invoice_id: 'inv-008',
            organization_id: 'org-123',
            invoice_number: 'INV-008',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Open,
            description: 'Open invoice due soon',
            hosted_invoice_url: 'https://example.com/invoice/8',
            pdf_url: 'https://example.com/invoice/8.pdf',
            due_date: '2026-03-08T00:00:00Z',
            amount_paid: 0,
            amount_total: 800,
            user_id: 'user-123',
            created_at: '2026-02-20T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-009',
            organization_id: 'org-123',
            invoice_number: 'INV-009',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Open,
            description: 'Future installment invoice (should be filtered out)',
            hosted_invoice_url: 'https://example.com/invoice/9',
            pdf_url: 'https://example.com/invoice/9.pdf',
            due_date: '2030-01-01T00:00:00Z',
            amount_paid: 0,
            amount_total: 1000,
            user_id: 'user-123',
            created_at: '2026-02-20T00:00:00Z',
            is_overdue: false,
          },
          {
            invoice_id: 'inv-010',
            organization_id: 'org-123',
            invoice_number: 'INV-010',
            transaction_type: TransactionType.Subscription,
            source_type: TransactionSourceType.Unknown,
            status: InvoiceStatus.Paid,
            description: 'Paid invoice',
            hosted_invoice_url: 'https://example.com/invoice/10',
            pdf_url: 'https://example.com/invoice/10.pdf',
            due_date: '2026-01-10T00:00:00Z',
            amount_paid: 950,
            amount_total: 950,
            user_id: 'user-123',
            created_at: '2025-12-28T00:00:00Z',
            is_overdue: false,
          },
        ],
      },
      { status: 200 }
    );
  }),
  // Check if billing info exists (used by Storefront)
  http.get(
    `*/v2/organizations/:organizationId/billing/info/exists`,
    async () => {
      await delay();
      return HttpResponse.json({ billing_info_exists: true }, { status: 200 });
    }
  ),

  http.get(`*/v2/organizations/:organizationId/billing/info`, async () => {
    await delay();
    return HttpResponse.json(billingInfo, { status: 200 });
  }),
  http.put(`*/v2/organizations/:organizationId/billing/info`, async () => {
    await delay();
    return HttpResponse.json(billingInfo, { status: 200 });
  }),
  http.post(
    `*/v2/organizations/:organizationId/billing/setup_payment`,
    async () => {
      await delay();
      return HttpResponse.json(
        { client_secret: 'seti_mock_secret' },
        { status: 200 }
      );
    }
  ),
  http.post(
    `*/v3/organizations/:organizationId/billing/setup_payment`,
    async () => {
      await delay();
      return HttpResponse.json(
        { client_secret: 'seti_mock_secret' },
        { status: 200 }
      );
    }
  ),

  // payment methods (specific routes before `:paymentMethodId`)
  http.get(
    `*/v2/organizations/:organizationId/billing/payment_methods`,
    async () => {
      await delay();
      return HttpResponse.json(paymentMethodsV1, { status: 200 });
    }
  ),
  http.get(
    `*/v3/organizations/:organizationId/billing/payment_methods`,
    async () => {
      await delay();
      return HttpResponse.json(paymentMethodsV3, { status: 200 });
    }
  ),
  http.post(
    `*/v2/organizations/:organizationId/billing/payment_methods/default`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.post(
    `*/v3/organizations/:organizationId/billing/payment_methods/default/stripe_id`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.post(
    `*/v3/organizations/:organizationId/billing/payment_methods/default`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.post(
    `*/v2/organizations/:organizationId/billing/payment_methods/:paymentMethodId`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.delete(
    `*/v2/organizations/:organizationId/billing/payment_methods/:paymentMethodId`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.patch(
    `*/v3/organizations/:organizationId/billing/payment_methods/:paymentMethodId`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),
  http.delete(
    `*/v3/organizations/:organizationId/billing/payment_methods/:paymentMethodId`,
    async () => {
      await delay();
      return new HttpResponse(null, { status: 204 });
    }
  ),

  http.post(
    `*/v2/organizations/:organizationId/billing/transaction/estimate`,
    async () => {
      await delay();
      return HttpResponse.json(taxesEstimation, { status: 200 });
    }
  ),
  http.get(
    `*/v3/organizations/:organizationId/billing/payment_terms`,
    async () => {
      await delay();
      return HttpResponse.json(paymentTerms, { status: 200 });
    }
  ),
  http.post(
    `*/v3/organizations/:organizationId/billing/payment_terms`,
    async () => {
      await delay();
      return HttpResponse.json(paymentTerms, { status: 200 });
    }
  ),
  http.post(
    `*/v3/organizations/:organizationId/billing/subscription/license-count`,
    async () => {
      await delay();
      return HttpResponse.json(licenseCountAmendment, { status: 200 });
    }
  ),
  http.get(
    `*/v3/organizations/:organizationId/billing/subscription/pending-changes`,
    async () => {
      await delay();
      return HttpResponse.json(noPendingSubscriptionChange, { status: 200 });
    }
  ),
];
